import {gql} from '@apollo/client';
import {
    type Filter,
    type GqlDownloadRequestPayload,
    type GqlRequest,
    GqlRequestBuilder,
    type IDownloadByLinkRequestBuilder,
    maxServerDownloadPageSize,
    type RequestPayload,
} from '@igp/shared/api';
import type {
    DebtResponsibleMasterAgent,
    QueryExportDebtResponsibleMasterAgentsArgs,
    QueryGetDebtResponsibleMasterAgentsArgs,
} from '@igp/shared/contract/pam';
import type {EntityType} from '@models';
import type {DocumentNode} from 'graphql';

import type {
    DebtResponsibleMasterAgentDownloadFields,
    DebtResponsibleMasterAgentQueryFields,
    DebtResponsibleMasterAgentQueryFilterKeys,
    DebtResponsibleMasterAgentTextFilterKeys,
} from './types';

export interface IDebtResponsibleMasterAgentMutationRequestBuilder {
    buildRequest(
        requestPayload: RequestPayload<DebtResponsibleMasterAgentQueryFields>
    ): GqlRequest<QueryGetDebtResponsibleMasterAgentsArgs>;
    buildAddDebtResponsibleMasterAgentMutation(): DocumentNode;
    buildRemoveDebtResponsibleMasterAgentMutation(): DocumentNode;
}

export class DebtResponsibleMasterAgentRequestBuilder
    extends GqlRequestBuilder<
        EntityType,
        QueryGetDebtResponsibleMasterAgentsArgs,
        DebtResponsibleMasterAgentQueryFields,
        DebtResponsibleMasterAgentQueryFilterKeys
    >
    implements
        IDebtResponsibleMasterAgentMutationRequestBuilder,
        IDownloadByLinkRequestBuilder<QueryExportDebtResponsibleMasterAgentsArgs, DebtResponsibleMasterAgentDownloadFields>
{
    buildDownloadRequest(
        downloadRequestPayload: GqlDownloadRequestPayload<DebtResponsibleMasterAgentDownloadFields>
    ): GqlRequest<QueryExportDebtResponsibleMasterAgentsArgs> {
        const filter = this.mapToFilterObject(downloadRequestPayload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: this.buildExportQuery(),
            variables: {
                report_name: downloadRequestPayload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: downloadRequestPayload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    public buildAddDebtResponsibleMasterAgentMutation(): DocumentNode {
        return gql`
            mutation AddDebtResponsibleMasterAgent($agent_id: String!) {
                addDebtResponsibleMasterAgent(agent_id: $agent_id) {
                    agent_id
                }
            }
        `;
    }

    public buildRemoveDebtResponsibleMasterAgentMutation(): DocumentNode {
        return gql`
            mutation RemoveDebtResponsibleMasterAgent($agent_id: String!) {
                removeDebtResponsibleMasterAgent(agent_id: $agent_id) {
                    agent_id
                }
            }
        `;
    }

    protected validateFilters(_filter: Filter<DebtResponsibleMasterAgentQueryFilterKeys>): void {}

    protected buildQuery(fields: DebtResponsibleMasterAgentQueryFields[]): DocumentNode {
        const requiredFields: DebtResponsibleMasterAgentQueryFields[] = ['items.agent_id', 'total_count'];
        const fieldsToFetch = [...new Set([...requiredFields, ...fields])];
        return gql`
            query GetDebtResponsibleMasterAgents($filter: DebtResponsibleMasterAgentsFilter, $sort: Sorting, $start: Int, $end: Int) {
                getDebtResponsibleMasterAgents(filter: $filter, sort: $sort, start: $start, end: $end) {
                    ${this.buildQueryFields(fieldsToFetch)}
                }
            }
        `;
    }

    protected buildFilter(filter: Filter<DebtResponsibleMasterAgentQueryFilterKeys>): QueryGetDebtResponsibleMasterAgentsArgs {
        return {
            filter: {
                text: this.getGQLTextFilter(
                    Object.keys(this.filterFieldsMapper).map((key: DebtResponsibleMasterAgentTextFilterKeys) =>
                        this.toGQLTextFilter(this.filterFieldsMapper[key], filter[key] as string)
                    )
                ),
            },
        };
    }

    private readonly filterFieldsMapper: Record<DebtResponsibleMasterAgentTextFilterKeys, (keyof DebtResponsibleMasterAgent)[]> = {
        agent_uid: ['agent_id'],
    };

    private buildExportQuery() {
        return gql`
            query ExportDebtResponsibleMasterAgents(
                $filter: DebtResponsibleMasterAgentsFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportDebtResponsibleMasterAgents(
                    filter: $filter
                    report_name: $report_name
                    fields: $fields
                    sort: $sort
                    end: $end
                    start: $start
                ) {
                    url
                }
            }
        `;
    }
}
